import { ApiError, normalizeApiError } from '@/api/errors'

// Тексты ошибок API — данные, а не код. Общий слой + перекрытия на фичу.
// Новая ручка = одна запись в ERROR_SCOPES, без изменения логики.
// Ключи структурированы под будущий переезд в i18n.

/**
 * Показывать ли сообщение бэка пользователю напрямую.
 * Ставим true, когда бэкенд подтвердит, что message — готовый русский текст.
 */
export const TRUST_BACKEND_MESSAGE = false

export interface ErrorScope {
	byCode?: Record<string, string>
	byStatus?: Record<number, string>
	network?: string
	serverError?: string
	fallback?: string
}

const COMMON: Required<Pick<ErrorScope, 'network' | 'serverError' | 'fallback'>> & ErrorScope = {
	byCode: {
		// Отказ смежного сервиса (SDP) — не ошибка конкретной фичи: приходит и от send-code,
		// и от verify-code, поэтому текст живёт здесь, а не дублируется в двух scope.
		SDP_UNAVAILABLE: 'Сервис временно недоступен. Попробуйте позже' // ← ТЕКСТ
	},
	byStatus: {},
	network: 'Нет соединения. Проверьте интернет и попробуйте ещё раз',
	serverError: 'Сервис временно недоступен. Попробуйте позже',
	fallback: 'Что-то пошло не так. Попробуйте ещё раз'
}

const INVALID_EMAIL = 'Введите корректный e-mail, например name@ya.ru'

// Обрыв сети на экранах входа показывает глобальная модалка (onNetworkError в main.ts),
// поэтому инлайн-текст под полем гасим пустой строкой — иначе пользователь получил бы
// одно и то же сообщение дважды.
const NETWORK_HANDLED_BY_DIALOG = ''

/**
 * Коды verify-code, после которых введённый код мёртв и нужен новый.
 * Список нужен не только для текста: на этих кодах снимаем кулдаун повторной отправки,
 * иначе совет «получите новый код» невыполним — кнопка ещё заблокирована таймером.
 */
export const OTP_NEEDS_NEW_CODE = ['OTP_EXPIRED', 'OTP_ATTEMPTS_EXCEEDED'] as const

export const ERROR_SCOPES = {
	authSendCode: {
		// Коды бэка пишем ровно как он их отдаёт: resolveErrorMessage сравнивает без
		// приведения регистра. byStatus ниже остаётся фолбэком для ответов без code.
		byCode: {
			INVALID_EMAIL: INVALID_EMAIL, // ← ТЕКСТ
			TERMS_NOT_ACCEPTED: 'Необходимо принять условия', // ← ТЕКСТ
			EMAIL_LIMIT_EXCEEDED: 'Превышено количество попыток ввода адреса e-mail. Попробуйте позже', // ← ТЕКСТ
			OTP_RATE_LIMITED: 'Слишком много попыток. Попробуйте позже' // ← ТЕКСТ
		},
		byStatus: {
			400: INVALID_EMAIL,
			422: INVALID_EMAIL,
			429: 'Слишком много попыток. Повторите позже'
		},
		network: NETWORK_HANDLED_BY_DIALOG,
		fallback: 'Не удалось отправить код. Попробуйте ещё раз'
	},
	authVerifyCode: {
		byCode: {
			INVALID_OTP: 'Неверный код. Проверьте цифры и попробуйте ещё раз', // ← ТЕКСТ
			OTP_EXPIRED: 'Срок действия кода истёк. Запросите новый код', // ← ТЕКСТ
			OTP_ATTEMPTS_EXCEEDED: 'Слишком много неверных попыток. Получите новый код', // ← ТЕКСТ
			// Легаси-код: отдавался до перехода на UPPER_SNAKE_CASE, оставляем до вывода из прода.
			invalid_param: 'Неверный код. Проверьте цифры и попробуйте еще раз.'
		},
		byStatus: {
			410: 'Код устарел. Запросите новый',
			429: 'Слишком много попыток. Повторите позже'
		},
		network: NETWORK_HANDLED_BY_DIALOG,
		fallback: 'Неверный код. Проверьте и попробуйте снова'
	},
	// Вход через провайдера: ошибки обмена тикета и коды из /auth/callback?error=.
	// Строку из ?error= не показываем как текст — её подставит кто угодно в ссылке.
	authOauth: {
		byCode: {
			OAUTH_CODE_INVALID: 'Не удалось войти. Попробуйте ещё раз.' // ← ТЕКСТ
		},
		fallback: 'Не удалось войти. Попробуйте ещё раз.' // ← ТЕКСТ
	}
	// TODO: сюда же переезжают тексты генерации (ERROR_CODES.IMAGE_SAFETY и др.),
	// когда будем трогать этот флоу.
} satisfies Record<string, ErrorScope>

/** Текст ошибки формата e-mail — используется и валидацией на клиенте, и маппингом 400/422. */
export const INVALID_EMAIL_MESSAGE = INVALID_EMAIL

/**
 * Превращает любую ошибку API в готовый текст для пользователя.
 * Порядок: код фичи → общий код → статус фичи → общий статус → 5xx → сеть →
 * сообщение бэка (если ему доверяем) → фолбэк фичи → общий фолбэк.
 */
export function resolveErrorMessage(error: unknown, scope: ErrorScope = {}): string {
	const apiError: ApiError = normalizeApiError(error)

	if (import.meta.env.DEV && apiError.raw) {
		console.warn('[api error]', { status: apiError.status, code: apiError.code, raw: apiError.raw })
	}

	if (apiError.code) {
		const byCode = scope.byCode?.[apiError.code] ?? COMMON.byCode?.[apiError.code]
		if (byCode) return byCode
	}

	if (apiError.status !== undefined) {
		const byStatus = scope.byStatus?.[apiError.status] ?? COMMON.byStatus?.[apiError.status]
		if (byStatus) return byStatus
		if (apiError.status >= 500) return scope.serverError ?? COMMON.serverError
	}

	// scope.network === '' — осознанное «текста нет, ошибку показывает модалка», поэтому ??, а не ||
	if (apiError.network) return scope.network ?? COMMON.network

	if (TRUST_BACKEND_MESSAGE && apiError.raw) return apiError.raw

	return scope.fallback ?? COMMON.fallback
}
